import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/lib/i18n";
import { VsHumanProvider } from "@/lib/vsHumanSocket";
import ScrollToTop from "@/components/ScrollToTop";
import OrientationGuard from "@/components/OrientationGuard";
import MobileFullscreen from "@/components/MobileFullscreen";

import Home from "@/pages/Home";
import Regulamento from "@/pages/Regulamento";
import TrainStart from "@/pages/TrainStart";
import DemoRedirect from "@/pages/DemoRedirect";
import GrandeFinalStart from "@/pages/GrandeFinalStart";
import CompetitionLogin from "@/pages/CompetitionLogin";
import CompetitionLobby from "@/pages/CompetitionLobby";
import GamePlay from "@/pages/GamePlay";
import Rankings from "@/pages/Rankings";
import CompetitionResults from "@/pages/CompetitionResults";
import InscriptionForm from "@/pages/InscriptionForm";
import DiplomaIndividual from "@/pages/DiplomaIndividual";
import DiplomaMassa from "@/pages/DiplomaMassa";
import AdminLogin from "@/pages/AdminLogin";
import AlunoLogin from "@/pages/AlunoLogin";

import EliminatoriasLanding from "@/pages/eliminatorias/EliminatoriasLanding";
import EliminatoriasLobby from "@/pages/eliminatorias/EliminatoriasLobby";
import EliminatoriasResetPassword from "@/pages/eliminatorias/EliminatoriasResetPassword";
import VsRobot from "@/pages/VsRobot";
import VsHumano from "@/pages/VsHumano";

import AdminLayout from "@/pages/admin/AdminLayout";import AdminDashboard from "@/pages/admin/AdminDashboard";import AdminMetrics from "@/pages/admin/MetricsPanel";
import AdminGames from "@/pages/admin/Games";
import AdminQuestions from "@/pages/admin/Questions";
import AdminCredentials from "@/pages/admin/Credentials";
import AdminTrainingUsers from "@/pages/admin/TrainingUsers";
import AdminCompetitionUsers from "@/pages/admin/CompetitionUsers";
import AdminDiplomas from "@/pages/admin/Diplomas";
import AdminMessages from "@/pages/admin/Messages";
import AdminRegulamento from "@/pages/admin/Regulamento";
import AdminHomeButtons from "@/pages/admin/HomeButtons";
import AdminTestZone from "@/pages/admin/TestZone";
import AdminIntro from "@/pages/admin/Intro";
import AdminInscricoes from "@/pages/admin/Inscricoes";
import AdminPagamentos from "@/pages/admin/Pagamentos";
import PayLater from "@/pages/PayLater";
import PedirFatura from "@/pages/PedirFatura";
import AdminResults from "@/pages/admin/Results";
import AdminEliminatoriasUsers from "@/pages/admin/EliminatoriasUsers";
import AdminEliminatorias from "@/pages/admin/Eliminatorias";
import AdminRobots from "@/pages/admin/Robots";
import AdminVsRobotUsers from "@/pages/admin/VsRobotUsers";
import AdminVsHumanUsers from "@/pages/admin/VsHumanUsers";
import AdminDemoPhrases from "@/pages/admin/DemoPhrases";
import AdminGrandeFinal from "@/pages/admin/GrandeFinalAdmin";
import AdminRoulette from "@/pages/admin/Roulette";
import AdminI18nDashboard from "@/pages/admin/I18nDashboard";
import AdminSearchAndEdit from "@/pages/admin/SearchAndEdit";
import AdminAdsManager from "@/pages/admin/AdsManager";
import SideAds from "@/components/SideAds";
import AdminMigration from "@/pages/admin/Migration";
import AdminPendingResults from "@/pages/admin/PendingResults";
import AdminMainSettings from "@/pages/admin/MainSettings";
import IntroSplash from "@/components/IntroSplash";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import GlobalVsHumanRematch from "@/components/GlobalVsHumanRematch";
import { refreshFromServer as refreshStudentAuth } from "@/lib/studentAuth";
import { preloadGameplayAssets } from "@/lib/assetPreloader";
import { bootOutbox } from "@/lib/resultOutbox";

// Validate any cached student JWT once on app boot.
refreshStudentAuth().catch(() => {});

// Eagerly preload all gameplay letter/star images so school networks with
// flaky WiFi never get a frame of "previous letter still visible" between
// rounds. Fires once at boot; subsequent calls reuse the same Promise.
preloadGameplayAssets();

// Boot the Result Outbox worker — drains queued results (e.g. from a
// previous tab that lost connectivity at end-of-game) and listens for the
// `online` event. See lib/resultOutbox.js for the full design notes.
bootOutbox();

/**
 * GamePlayRouteKey — wrapper around <GamePlay/> that gives the component a
 * unique key whenever the VS HUMANO session_id in the query string changes.
 * This forces a full remount when a rematch starts, so the freshly-written
 * `vsh_session` in sessionStorage is re-read from a clean state instead of
 * being ignored by the already-mounted GamePlay instance.
 */
function GamePlayRouteKey() {
  const { gameId } = useParams();
  const [sp] = useSearchParams();
  const vsh = sp.get("vsh") || "";
  return <GamePlay key={`${gameId}:${vsh}`} />;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <VsHumanProvider>
          <BrowserRouter>
          <ScrollToTop />
          <OrientationGuard />
          <MobileFullscreen />
          <GlobalVsHumanRematch />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/regulamento" element={<Regulamento />} />
            <Route path="/treinar" element={<TrainStart />} />
            <Route path="/demo" element={<DemoRedirect />} />
            <Route path="/treino/grande-final" element={<GrandeFinalStart />} />
            <Route path="/competir" element={<CompetitionLogin />} />
            <Route path="/lobby/:gameId" element={<CompetitionLobby />} />
            <Route path="/jogar/:gameId" element={<GamePlayRouteKey />} />
            <Route path="/resultados" element={<Rankings />} />
            <Route path="/resultados-competicao" element={<CompetitionResults />} />
            <Route path="/resultados-grande-final" element={<CompetitionResults />} />
            <Route path="/rankings" element={<Rankings />} />
            <Route path="/inscrever-escola" element={<InscriptionForm />} />
            <Route path="/pagar/:token" element={<PayLater />} />
            <Route path="/pedir-fatura/:id" element={<PedirFatura />} />
            <Route path="/diploma-individual" element={<DiplomaIndividual />} />
            <Route path="/diploma-massa" element={<DiplomaMassa />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/aluno-login" element={<AlunoLogin />} />
            <Route path="/eliminatorias" element={<EliminatoriasLanding />} />
            <Route path="/eliminatorias/lobby" element={<EliminatoriasLobby />} />
            <Route path="/eliminatorias/reset-password" element={<EliminatoriasResetPassword />} />
            <Route path="/vs-robot" element={<VsRobot />} />
            <Route path="/vs-humano" element={<VsHumano />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="jogos" element={<AdminGames />} />
              <Route path="credenciais" element={<AdminCredentials />} />
              <Route path="utilizadores-treino" element={<AdminTrainingUsers />} />
              <Route path="utilizadores-competicao" element={<AdminCompetitionUsers />} />
              <Route path="perguntas" element={<AdminQuestions />} />
              <Route path="perguntas/:gameId" element={<AdminQuestions />} />
              <Route path="diplomas" element={<AdminDiplomas />} />
              <Route path="mensagens" element={<AdminMessages />} />
              <Route path="regulamento" element={<AdminRegulamento />} />
              <Route path="definicoes-principal" element={<AdminMainSettings />} />
              <Route path="definicoes-branding" element={<Navigate to="/admin/definicoes-principal" replace />} />
              <Route path="botoes" element={<Navigate to="/admin/definicoes-principal" replace />} />
              <Route path="rodape" element={<Navigate to="/admin/definicoes-principal" replace />} />
              <Route path="testes" element={<AdminTestZone />} />
              <Route path="intro" element={<AdminIntro />} />
              <Route path="inscricoes" element={<AdminInscricoes />} />
              <Route path="pagamentos" element={<AdminPagamentos />} />
              <Route path="resultados" element={<AdminResults />} />
              <Route path="definicoes" element={<Navigate to="/admin/definicoes-principal" replace />} />
              <Route path="eliminatorias-escolares" element={<AdminEliminatoriasUsers />} />
              <Route path="eliminatorias" element={<AdminEliminatorias />} />
              <Route path="robots" element={<AdminRobots />} />
              <Route path="utilizadores-vs-robot" element={<AdminVsRobotUsers />} />
              <Route path="utilizadores-vs-humano" element={<AdminVsHumanUsers />} />
              <Route path="demo-frases" element={<AdminDemoPhrases />} />
              <Route path="grande-final" element={<AdminGrandeFinal />} />
              <Route path="roleta" element={<AdminRoulette />} />
              <Route path="i18n" element={<AdminI18nDashboard />} />
              <Route path="pesquisar-editar" element={<AdminSearchAndEdit />} />
              <Route path="publicidade" element={<AdminAdsManager />} />
              <Route path="migracao" element={<AdminMigration />} />
              <Route path="resultados-pendentes" element={<AdminPendingResults />} />
              <Route path="metricas" element={<AdminMetrics />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <IntroSplash />
          <PWAInstallBanner />
          <SideAdsGate />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </VsHumanProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;

// Renders the side ads globally on every non-admin route. Sits outside <Routes>
// (so it doesn't rerender on navigation) but reads the current pathname via
// useLocation to hide itself under /admin/*.
function SideAdsGate() {
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
      const sw = window.screen && window.screen.width ? window.screen.width : window.innerWidth;
      const sh = window.screen && window.screen.height ? window.screen.height : window.innerHeight;
      const shortSide = Math.min(sw, sh);
      return !!coarse && shortSide < 900;
    } catch { return false; }
  });
  React.useEffect(() => {
    const recheck = () => {
      try {
        const coarse = window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
        const sw = window.screen && window.screen.width ? window.screen.width : window.innerWidth;
        const sh = window.screen && window.screen.height ? window.screen.height : window.innerHeight;
        const shortSide = Math.min(sw, sh);
        setIsMobile(!!coarse && shortSide < 900);
      } catch { /* ignore */ }
    };
    window.addEventListener("resize", recheck);
    window.addEventListener("orientationchange", recheck);
    return () => {
      window.removeEventListener("resize", recheck);
      window.removeEventListener("orientationchange", recheck);
    };
  }, []);
  if (isMobile) return null;                                    // always hidden on mobile devices
  if (location.pathname.startsWith("/admin")) return null;
  // Hide side ads during online gameplay screens (VS Tempo, VS Robot, VS Humano)
  if (location.pathname.startsWith("/jogar/")) return null;
  return <SideAds />;
}
